import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Save,
  Assignment,
  History,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { extractClinicalConductHistory } from "@/fluxvet/utils/questionnaireResponseHelper";
import { HistoryModal } from "./HistoryModal";

/**
 * Gera um ID único para os itens
 */
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

interface ClinicalConduct {
  id: string;
  description: string;
  status?: "success" | "failed" | "in_progress"; // Status da conduta: sucesso, não teve sucesso ou em andamento
  outcomeDescription?: string; // Descrição do resultado/desfecho da conduta (sempre disponível)
}

interface ClinicalConductsSectionProps {
  control: any;
  watch: any;
  setValue: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Condutas Clínicas
 * Permite adicionar, editar e remover múltiplas condutas clínicas
 * Similar ao TreatmentsSection, com formulário à esquerda e lista à direita
 */
export const ClinicalConductsSection: React.FC<ClinicalConductsSectionProps> = ({
  control,
  watch,
  setValue,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados
  const clinicalConductHistory = useMemo(
    () => extractClinicalConductHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Observa a lista do formulário
  const clinicalConducts: ClinicalConduct[] = watch("clinicalConducts") || [];

  // Estado para o formulário de adição (apenas descrição)
  const [conductFormData, setConductFormData] = useState({
    description: "",
  });

  // Estado para controlar quais cards estão em modo de edição
  const [editingCardIds, setEditingCardIds] = useState<Set<string>>(new Set());

  // Estado para controlar valores temporários de edição em cada card
  // Armazena os valores locais antes de salvar
  const [cardEditStates, setCardEditStates] = useState<
    Record<
      string,
      {
        status?: "success" | "failed" | "in_progress";
        outcomeDescription: string;
      }
    >
  >({});

  // Estado para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
  });

  /**
   * Adiciona nova conduta clínica à lista
   * Nova conduta sempre começa sem status definido
   */
  const handleAdd = () => {
    if (!conductFormData.description.trim()) {
      return;
    }

    const newConduct: ClinicalConduct = {
      id: generateId(),
      description: conductFormData.description,
      // Não define status inicial - será definido pelo usuário
    };

    setValue("clinicalConducts", [...clinicalConducts, newConduct]);
    resetForm();
  };

  /**
   * Obtém o estado de edição de um card
   * Se não existir no estado, retorna os valores atuais da conduta
   */
  const getCardEditState = (conduct: ClinicalConduct) => {
    if (!cardEditStates[conduct.id]) {
      // Retorna valores padrão baseados na conduta atual
      return {
        status: conduct.status,
        outcomeDescription: conduct.outcomeDescription || "",
      };
    }
    return cardEditStates[conduct.id];
  };

  /**
   * Atualiza o estado temporário de edição de um card
   */
  const updateCardEditState = (
    id: string,
    updates: Partial<{
      status?: "success" | "failed" | "in_progress";
      outcomeDescription: string;
    }>
  ) => {
    setCardEditStates((prev) => ({
      ...prev,
      [id]: {
        ...getCardEditState(
          clinicalConducts.find((c) => c.id === id) || {
            id,
            description: "",
          }
        ),
        ...updates,
      },
    }));
  };

  /**
   * Inicia a edição de um card
   */
  const handleStartEdit = (id: string) => {
    setEditingCardIds((prev) => new Set(prev).add(id));
    // Inicializa o estado de edição se não existir
    const conduct = clinicalConducts.find((c) => c.id === id);
    if (conduct && !cardEditStates[id]) {
      setCardEditStates((prev) => ({
        ...prev,
        [id]: {
          status: conduct.status,
          outcomeDescription: conduct.outcomeDescription || "",
        },
      }));
    }
  };

  /**
   * Cancela a edição de um card
   */
  const handleCancelEdit = (id: string) => {
    setEditingCardIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    // Remove o estado temporário
    setCardEditStates((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  /**
   * Salva as alterações de um card no formulário
   */
  const handleSaveCard = (id: string) => {
    const editState = cardEditStates[id];
    if (!editState) return;

    const updatedConducts = clinicalConducts.map((conduct) =>
      conduct.id === id
        ? {
            ...conduct,
            status: editState.status,
            outcomeDescription: editState.outcomeDescription,
          }
        : conduct
    );

    setValue("clinicalConducts", updatedConducts);
    // Remove o estado temporário e sai do modo de edição
    setCardEditStates((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
    setEditingCardIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  /**
   * Remove uma conduta da lista
   */
  const handleDelete = (id: string) => {
    const filtered = clinicalConducts.filter((conduct) => conduct.id !== id);
    setValue("clinicalConducts", filtered);
  };

  /**
   * Reseta o formulário de adição
   */
  const resetForm = () => {
    setConductFormData({
      description: "",
    });
  };

  return (
    <>
      <Box>
        {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Assignment sx={{ color: "#00A884" }} />
            <Typography variant="h6" fontWeight={600}>
              Condutas Clínicas
            </Typography>
            {clinicalConducts.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                ({clinicalConducts.length}{" "}
                {clinicalConducts.length === 1 ? "item" : "itens"})
              </Typography>
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<History />}
            onClick={() => setHistoryModal({ isOpen: true })}
            sx={{
              textTransform: "none",
              fontSize: "0.85rem",
              color: "#666",
              "&:hover": {
                color: "#00A884",
                bgcolor: "rgba(0, 168, 132, 0.08)",
              },
            }}
          >
            Ver histórico
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {/* Coluna Esquerda: Formulário (40%) */}
          <Grid item xs={12} md={4.8}>
            <Paper
              key={`conduct-form-${clinicalConducts.length}`}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                bgcolor: "#fafafa",
                height: "100%",
                minHeight: 420,
              }}
            >
              <Stack spacing={2} height="100%">
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="#00A884"
                >
                  ➕ Nova Conduta
                </Typography>

                <Divider />

                <TextField
                  fullWidth
                  label="Descrição da conduta clínica *"
                  placeholder="Ex: Dieta leve por 3 dias, medicações prescritas, orientações ao tutor, data de retorno..."
                  value={conductFormData.description}
                  onChange={(e) =>
                    setConductFormData({
                      description: e.target.value,
                    })
                  }
                  multiline
                  rows={10}
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#fff",
                    },
                  }}
                />

                <Box sx={{ flexGrow: 1 }} />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAdd}
                    sx={{
                      bgcolor: "#00A884",
                      "&:hover": { bgcolor: "#008c6e" },
                      textTransform: "none",
                    }}
                  >
                    Adicionar
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Coluna Direita: Lista de itens (60%) */}
          <Grid item xs={12} md={7.2}>
            {clinicalConducts.length > 0 ? (
              <Stack spacing={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  CONDUTAS CLÍNICAS REGISTRADAS
                </Typography>
                <Box
                  sx={{
                    maxHeight: 420,
                    overflowY: "auto",
                    pr: 0.5,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "#00A884",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Stack spacing={2}>
                    {clinicalConducts.map((conduct) => {
                      const isEditing = editingCardIds.has(conduct.id);
                      const editState = getCardEditState(conduct);
                      const hasChanges =
                        isEditing &&
                        (editState.status !== conduct.status ||
                          editState.outcomeDescription !==
                            (conduct.outcomeDescription || ""));

                      return (
                        <Card
                          key={conduct.id}
                          sx={{
                            border: "1px solid #e0e0e0",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "#00A884",
                              boxShadow: "0 4px 12px rgba(0, 168, 132, 0.15)",
                            },
                          }}
                        >
                          <CardContent>
                            <Stack spacing={2}>
                              {/* Cabeçalho com título e botões */}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    color="#00A884"
                                  >
                                    Conduta #
                                    {clinicalConducts.indexOf(conduct) + 1}
                                  </Typography>
                                  {/* Indicador visual de status atual */}
                                  {conduct.status && (
                                    <Chip
                                      icon={
                                        conduct.status === "success" ? (
                                          <CheckCircle fontSize="small" />
                                        ) : conduct.status === "failed" ? (
                                          <Cancel fontSize="small" />
                                        ) : (
                                          <HourglassEmpty fontSize="small" />
                                        )
                                      }
                                      label={
                                        conduct.status === "success"
                                          ? "Sucesso"
                                          : conduct.status === "failed"
                                          ? "Não teve sucesso"
                                          : "Em andamento"
                                      }
                                      size="small"
                                      color={
                                        conduct.status === "success"
                                          ? "success"
                                          : conduct.status === "failed"
                                          ? "error"
                                          : "warning"
                                      }
                                      sx={{
                                        height: 24,
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                      }}
                                    />
                                  )}
                                </Stack>
                                <Stack direction="row" spacing={0.5}>
                                  {isEditing ? (
                                    <>
                                      {/* Botão de salvar - só aparece se houver mudanças */}
                                      {hasChanges && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={<Save />}
                                          onClick={() =>
                                            handleSaveCard(conduct.id)
                                          }
                                          sx={{
                                            bgcolor: "#00A884",
                                            "&:hover": { bgcolor: "#008c6e" },
                                            textTransform: "none",
                                            fontSize: "0.75rem",
                                            px: 1.5,
                                          }}
                                        >
                                          Salvar
                                        </Button>
                                      )}
                                      {/* Botão de cancelar */}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          handleCancelEdit(conduct.id)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "0.75rem",
                                          px: 1.5,
                                        }}
                                      >
                                        Cancelar
                                      </Button>
                                    </>
                                  ) : (
                                    /* Botão de editar - aparece quando não está em modo de edição */
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Edit />}
                                      onClick={() =>
                                        handleStartEdit(conduct.id)
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        px: 1.5,
                                        borderColor: "#00A884",
                                        color: "#00A884",
                                        "&:hover": {
                                          borderColor: "#008c6e",
                                          bgcolor: "rgba(0, 168, 132, 0.08)",
                                        },
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(conduct.id)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        color: "#f44336",
                                        bgcolor: "rgba(244, 67, 54, 0.08)",
                                      },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>

                              <Divider />

                              {/* Descrição da conduta */}
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {conduct.description}
                              </Typography>

                              {/* Seção de Status - só aparece quando está em modo de edição */}
                              {isEditing && (
                                <Box
                                  sx={{
                                    bgcolor: "rgba(0, 168, 132, 0.05)",
                                    p: 2,
                                    borderRadius: 1,
                                    border: "1px solid rgba(0, 168, 132, 0.2)",
                                  }}
                                >
                                  <Stack spacing={1.5}>
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color="#00A884"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      Status da Conduta:
                                    </Typography>
                                    <FormControl
                                      component="fieldset"
                                      size="small"
                                    >
                                      <RadioGroup
                                        row
                                        value={editState.status || ""}
                                        onChange={(e) =>
                                          updateCardEditState(conduct.id, {
                                            status: e.target.value as
                                              | "success"
                                              | "failed"
                                              | "in_progress",
                                            // Mantém a descrição independente do status
                                            outcomeDescription:
                                              editState.outcomeDescription,
                                          })
                                        }
                                      >
                                        <FormControlLabel
                                          value="in_progress"
                                          control={<Radio size="small" />}
                                          label="Em andamento"
                                        />
                                        <FormControlLabel
                                          value="success"
                                          control={<Radio size="small" />}
                                          label="Sucesso"
                                        />
                                        <FormControlLabel
                                          value="failed"
                                          control={<Radio size="small" />}
                                          label="Não teve sucesso"
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Stack>
                                </Box>
                              )}

                              {/* Seção de Descrição do Resultado - visível apenas em edição ou se preenchida */}
                              {(isEditing ||
                                conduct.outcomeDescription ||
                                editState.outcomeDescription) && (
                                <Box
                                  sx={{
                                    bgcolor: "rgba(76, 175, 80, 0.08)",
                                    p: 2,
                                    borderRadius: 1,
                                    borderLeft: "3px solid #4caf50",
                                  }}
                                >
                                  <Stack spacing={1.5}>
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color="#4caf50"
                                    >
                                      Descrição do Resultado/Desfecho:
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={3}
                                      value={
                                        isEditing
                                          ? editState.outcomeDescription
                                          : conduct.outcomeDescription || ""
                                      }
                                      onChange={(e) => {
                                        if (isEditing) {
                                          updateCardEditState(conduct.id, {
                                            outcomeDescription: e.target.value,
                                          });
                                        } else {
                                          // Se não está em modo de edição, salva diretamente
                                          const updatedConducts =
                                            clinicalConducts.map((c) =>
                                              c.id === conduct.id
                                                ? {
                                                    ...c,
                                                    outcomeDescription:
                                                      e.target.value,
                                                  }
                                                : c
                                            );
                                          setValue(
                                            "clinicalConducts",
                                            updatedConducts
                                          );
                                        }
                                      }}
                                      placeholder="Ex: Conduta realizada com sucesso, paciente respondeu bem ao tratamento..."
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          bgcolor: "#fff",
                                        },
                                      }}
                                    />
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  border: "1px dashed #e0e0e0",
                  borderRadius: 2,
                  minHeight: 420,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Assignment sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma conduta clínica adicionada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Preencha o campo ao lado e clique em "Adicionar"
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Modal de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false })}
        title="Histórico - Condutas Clínicas"
        history={clinicalConductHistory}
      />
    </>
  );
};

export default ClinicalConductsSection;

